import React, { createContext, useContext, useReducer, useEffect, useMemo, ReactNode } from 'react';
import { scoreEpic } from '../utils/scoring';
import { createSession } from '../utils/sessionManager';
import { STORAGE_KEY, SESSIONS_KEY, PROJECTS_KEY, DEFAULT_PROJECT } from '../utils/constants';
import { Epic, Project, Session, EpicLane, User } from '../types';

// ============================================================================
// TYPES
// ============================================================================

export interface AppFilters {
    lane: string[];       // Multi-select: Now, Next, Later, Unassigned, Done
    owner: string[];
    arq: string[];
    sgmOnly: boolean;
    priority: string[];   // Multi-select: High, Medium, Low
    projectId: string[];  // Multi-select: project IDs
}

export interface AppState {
    // Data
    epics: Epic[];
    projects: Project[];
    sessions: Session[];
    users: User[];

    // UI State
    selectedId: string | null;
    selectedProjectIds: string[];
    activeView: string;
    search: string;
    theme: 'light' | 'dark';
    filters: AppFilters;
    isSidebarOpen: boolean;
}

export const ActionTypes = {
    // Epics
    SET_EPICS: 'SET_EPICS',
    UPDATE_EPIC: 'UPDATE_EPIC',
    ADD_EPIC: 'ADD_EPIC',
    DELETE_EPIC: 'DELETE_EPIC',
    MOVE_EPIC_TO_LANE: 'MOVE_EPIC_TO_LANE',

    // Projects
    SET_PROJECTS: 'SET_PROJECTS',
    ADD_PROJECT: 'ADD_PROJECT',
    UPDATE_PROJECT: 'UPDATE_PROJECT',
    DELETE_PROJECT: 'DELETE_PROJECT',
    SET_SELECTED_PROJECT: 'SET_SELECTED_PROJECT',

    // Users
    SET_USERS: 'SET_USERS',

    // Selection
    SET_SELECTED_ID: 'SET_SELECTED_ID',
    CLEAR_SELECTION: 'CLEAR_SELECTION',

    // View
    SET_ACTIVE_VIEW: 'SET_ACTIVE_VIEW',
    SET_SEARCH: 'SET_SEARCH',

    // Theme
    SET_THEME: 'SET_THEME',
    TOGGLE_THEME: 'TOGGLE_THEME',

    // Filters
    SET_FILTERS: 'SET_FILTERS',
    RESET_FILTERS: 'RESET_FILTERS',

    // Sessions
    SET_SESSIONS: 'SET_SESSIONS',
    SAVE_SESSION: 'SAVE_SESSION',
    DELETE_SESSION: 'DELETE_SESSION',

    // UI
    TOGGLE_SIDEBAR: 'TOGGLE_SIDEBAR',
} as const;

export type AppAction =
    | { type: typeof ActionTypes.SET_EPICS; payload: Epic[] }
    | { type: typeof ActionTypes.UPDATE_EPIC; payload: { id: string; updates: Partial<Epic> } }
    | { type: typeof ActionTypes.ADD_EPIC; payload: Epic }
    | { type: typeof ActionTypes.DELETE_EPIC; payload: string }
    | { type: typeof ActionTypes.MOVE_EPIC_TO_LANE; payload: { id: string; lane: EpicLane } }
    | { type: typeof ActionTypes.SET_PROJECTS; payload: Project[] }
    | { type: typeof ActionTypes.ADD_PROJECT; payload: Project }
    | { type: typeof ActionTypes.UPDATE_PROJECT; payload: { id: string; updates: Partial<Project> } }
    | { type: typeof ActionTypes.DELETE_PROJECT; payload: string }
    | { type: typeof ActionTypes.SET_SELECTED_PROJECT; payload: string[] }
    | { type: typeof ActionTypes.SET_USERS; payload: User[] }
    | { type: typeof ActionTypes.SET_SELECTED_ID; payload: string | null }
    | { type: typeof ActionTypes.CLEAR_SELECTION }
    | { type: typeof ActionTypes.SET_ACTIVE_VIEW; payload: string }
    | { type: typeof ActionTypes.SET_SEARCH; payload: string }
    | { type: typeof ActionTypes.SET_THEME; payload: 'light' | 'dark' }
    | { type: typeof ActionTypes.TOGGLE_THEME }
    | { type: typeof ActionTypes.SET_FILTERS; payload: Partial<AppFilters> }
    | { type: typeof ActionTypes.RESET_FILTERS }
    | { type: typeof ActionTypes.SET_SESSIONS; payload: Session[] }
    | { type: typeof ActionTypes.SAVE_SESSION; payload: string } // Payload is label
    | { type: typeof ActionTypes.DELETE_SESSION; payload: string }
    | { type: typeof ActionTypes.TOGGLE_SIDEBAR };


// ============================================================================
// INITIAL STATE
// ============================================================================

const defaultFilters: AppFilters = {
    lane: [],        // Empty = All lanes
    owner: [],
    arq: [],
    sgmOnly: false,
    priority: [],    // Empty = All priorities
    projectId: [],   // Empty = All projects
};

export const initialState: AppState = {
    // Data
    epics: [],
    projects: [],
    sessions: [],
    users: [],

    // UI State
    selectedId: null,
    selectedProjectIds: [],
    activeView: 'dashboard',
    search: '',
    theme: 'light',
    filters: defaultFilters,
    isSidebarOpen: false,
};

// ============================================================================
// REDUCER
// ============================================================================

function appReducer(state: AppState, action: AppAction): AppState {
    switch (action.type) {
        // Epics
        case ActionTypes.SET_EPICS:
            const uniqueEpics = Array.from(new Map(action.payload.map(epic => [epic.id, epic])).values());
            return { ...state, epics: uniqueEpics };

        case ActionTypes.UPDATE_EPIC:
            return {
                ...state,
                epics: state.epics.map(epic =>
                    epic.id === action.payload.id
                        ? { ...epic, ...action.payload.updates, ...scoreEpic({ ...epic, ...action.payload.updates } as Epic) }
                        : epic
                ),
            };

        case ActionTypes.ADD_EPIC:
            const newEpicValue = {
                ...action.payload,
                // id: `epic-${Date.now()}`, // ID should be generated by API or passed in payload if using local ID
            };
            const scoredNewEpic = { ...newEpicValue, ...scoreEpic(newEpicValue) };
            return { ...state, epics: [...state.epics, scoredNewEpic] };

        case ActionTypes.DELETE_EPIC:
            return {
                ...state,
                epics: state.epics.filter(e => e.id !== action.payload),
                selectedId: state.selectedId === action.payload ? null : state.selectedId,
            };

        case ActionTypes.MOVE_EPIC_TO_LANE:
            return {
                ...state,
                epics: state.epics.map(epic =>
                    epic.id === action.payload.id
                        ? { ...epic, lane: action.payload.lane, ...scoreEpic({ ...epic, lane: action.payload.lane }) }
                        : epic
                ),
            };

        // Projects
        case ActionTypes.SET_PROJECTS:
            return { ...state, projects: action.payload };

        case ActionTypes.ADD_PROJECT:
            // Ensure ID exists
            const projectToAdd = {
                ...action.payload,
                // id: action.payload.id || `project-${Date.now()}`,
                createdAt: action.payload.createdAt || new Date().toISOString(),
                updatedAt: action.payload.updatedAt || new Date().toISOString(),
            };
            return { ...state, projects: [...state.projects, projectToAdd] };

        case ActionTypes.UPDATE_PROJECT:
            return {
                ...state,
                projects: state.projects.map(project =>
                    project.id === action.payload.id
                        ? { ...project, ...action.payload.updates, updatedAt: new Date().toISOString() }
                        : project
                ),
            };

        case ActionTypes.DELETE_PROJECT:
            return {
                ...state,
                projects: state.projects.filter(p => p.id !== action.payload),
                epics: state.epics.filter(e => e.projectId !== action.payload),
                selectedProjectIds: state.selectedProjectIds.filter(id => id !== action.payload),
            };

        case ActionTypes.SET_SELECTED_PROJECT:
            return { ...state, selectedProjectIds: action.payload };

        // Users
        case ActionTypes.SET_USERS:
            return { ...state, users: action.payload };

        // Selection
        case ActionTypes.SET_SELECTED_ID:
            return { ...state, selectedId: action.payload };

        case ActionTypes.CLEAR_SELECTION:
            return { ...state, selectedId: null };

        // View
        case ActionTypes.SET_ACTIVE_VIEW:
            return { ...state, activeView: action.payload };

        case ActionTypes.SET_SEARCH:
            return { ...state, search: action.payload };

        // Theme
        case ActionTypes.SET_THEME:
            return { ...state, theme: action.payload };

        case ActionTypes.TOGGLE_THEME:
            return { ...state, theme: state.theme === 'dark' ? 'light' : 'dark' };

        // Filters
        case ActionTypes.SET_FILTERS:
            return { ...state, filters: { ...state.filters, ...action.payload } };

        case ActionTypes.RESET_FILTERS:
            return { ...state, filters: defaultFilters };

        // Sessions
        case ActionTypes.SET_SESSIONS:
            return { ...state, sessions: action.payload };

        case ActionTypes.SAVE_SESSION:
            // Need a valid user ID for session. Assuming 'local' for unsaved sessions or user hook should provide it?
            // The reducer doesn't have access to auth context.
            // Using a placeholder ID 'local-user' since this action is primarily local state.
            // This suggests ActionTypes.SAVE_SESSION might be deprecated if we move completely to API sessions,
            // but for local persistence we keep it.
            const newSession = createSession(state.epics, 'local-user', action.payload);
            const updatedSessions = [...state.sessions, newSession].slice(-52);
            return { ...state, sessions: updatedSessions };

        case ActionTypes.DELETE_SESSION:
            return {
                ...state,
                sessions: state.sessions.filter(s => s.id !== action.payload),
            };

        // UI
        case ActionTypes.TOGGLE_SIDEBAR:
            return { ...state, isSidebarOpen: !state.isSidebarOpen };

        default:
            return state;
    }
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function loadFromStorage<T>(key: string, fallback: T): T {
    try {
        const raw = localStorage.getItem(key);
        return raw ? JSON.parse(raw) : fallback;
    } catch {
        return fallback;
    }
}

function saveToStorage<T>(key: string, value: T) {
    try {
        localStorage.setItem(key, JSON.stringify(value));
    } catch {
        // Ignore storage errors
    }
}

// ============================================================================
// CONTEXT
// ============================================================================

interface AppContextValue {
    state: AppState;
    dispatch: React.Dispatch<AppAction>;
    actions: {
        setEpics: (epics: Epic[]) => void;
        updateEpic: (id: string, updates: Partial<Epic>) => void;
        addEpic: (epic: Epic) => void;
        deleteEpic: (id: string) => void;
        moveEpicToLane: (id: string, lane: EpicLane) => void;
        setProjects: (projects: Project[]) => void;
        addProject: (project: Project) => void;
        updateProject: (id: string, updates: Partial<Project>) => void;
        deleteProject: (id: string) => void;
        setSelectedProjects: (ids: string[]) => void;
        setUsers: (users: User[]) => void;
        setSelectedId: (id: string | null) => void;
        clearSelection: () => void;
        setActiveView: (view: string) => void;
        setSearch: (search: string) => void;
        setTheme: (theme: 'light' | 'dark') => void;
        toggleTheme: () => void;
        setFilters: (filters: Partial<AppFilters>) => void;
        resetFilters: () => void;
        setSessions: (sessions: Session[]) => void;
        saveSession: (label: string) => void;
        deleteSession: (id: string) => void;
        toggleSidebar: () => void;
    };
    computed: {
        filteredEpics: Epic[];
        uniqueOwners: string[];
        previousSession: Session | null;
        getPreviousScore: (epicId: string) => number | undefined;
        activeFilterCount: number;
        projectsWithStats: (Project & { epicCount: number; avgWsjf: number; laneDistribution: { now: number; next: number; later: number; unassigned: number; done: number }; doneCount: number })[];
    };
}

const AppContext = createContext<AppContextValue | null>(null);

export function AppProvider({ children, defaultEpics = [] }: { children: ReactNode; defaultEpics?: Epic[] }) {
    const [state, dispatch] = useReducer(appReducer, {
        ...initialState,
        epics: defaultEpics,
    });

    // Load from localStorage on mount
    useEffect(() => {
        const savedEpics = loadFromStorage<Epic[]>(STORAGE_KEY, []);
        const savedSessions = loadFromStorage<Session[]>(SESSIONS_KEY, []);
        const savedProjects = loadFromStorage<Project[]>(PROJECTS_KEY, []);

        // Load or create default project
        let loadedProjects = savedProjects;
        if (savedProjects.length > 0) {
            dispatch({ type: ActionTypes.SET_PROJECTS, payload: savedProjects });
        } else {
            loadedProjects = [DEFAULT_PROJECT];
            dispatch({ type: ActionTypes.SET_PROJECTS, payload: loadedProjects });
        }

        if (savedEpics && savedEpics.length > 0) {
            const validProjectIds = new Set(loadedProjects.map(p => String(p.id)));

            // 1. Re-score ALL epics
            // 2. Validate Project IDs (Migrate orphans to default)
            const healedEpics = savedEpics.map(e => {
                const projectIdToCheck = String(e.projectId || '');
                let pid = projectIdToCheck;

                if (!projectIdToCheck || !validProjectIds.has(projectIdToCheck)) {
                    const fallbackId = loadedProjects.length > 0 ? String(loadedProjects[0].id) : DEFAULT_PROJECT.id;
                    // console.warn(`Migrating epic ${e.id} from missing/invalid project '${projectIdToCheck}' to '${fallbackId}'`);
                    pid = fallbackId;
                }

                // Ensure numeric inputs
                const numericEpic: Epic = {
                    ...e,
                    projectId: pid,
                    business: Number(e.business) || 0,
                    time: Number(e.time) || 0,
                    security: Number(e.security) || 0,
                    ops: Number(e.ops) || 0,
                    confidence: Number(e.confidence) || 1,
                    effort: (Number(e.effort) || 1) as 1 | 2 | 3 | 5,
                    lane: e.lane as EpicLane
                };

                // Re-calculate Score
                const scores = scoreEpic(numericEpic);

                return {
                    ...numericEpic,
                    ...scores,
                    wsjf: Number(scores.wsjf) || 0,
                    cod: Number(scores.cod) || 0
                };
            });

            // Deduplicate by ID
            const uniqueEpics = Array.from(new Map(healedEpics.map(e => [e.id, e])).values());

            // console.log("Data Healed:", { count: uniqueEpics.length, sample: uniqueEpics[0] });
            saveToStorage(STORAGE_KEY, uniqueEpics);
            dispatch({ type: ActionTypes.SET_EPICS, payload: uniqueEpics });
        } else if (defaultEpics.length > 0) {
            const initializedEpics = defaultEpics.map(e => {
                const scores = scoreEpic(e);
                return {
                    ...e,
                    projectId: e.projectId || DEFAULT_PROJECT.id,
                    ...scores
                };
            });
            dispatch({ type: ActionTypes.SET_EPICS, payload: initializedEpics });
        }

        if (savedSessions.length > 0) {
            dispatch({ type: ActionTypes.SET_SESSIONS, payload: savedSessions });
        }

        // Load theme preference
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        const savedTheme = localStorage.getItem('theme');
        dispatch({ type: ActionTypes.SET_THEME, payload: (savedTheme as 'light' | 'dark') || (prefersDark ? 'dark' : 'light') });
    }, [defaultEpics]);

    // Persist epics to localStorage
    useEffect(() => {
        if (state.epics.length > 0) {
            saveToStorage(STORAGE_KEY, state.epics);
        }
    }, [state.epics]);

    // Persist sessions to localStorage
    useEffect(() => {
        saveToStorage(SESSIONS_KEY, state.sessions);
    }, [state.sessions]);

    // Persist projects to localStorage
    useEffect(() => {
        if (state.projects.length > 0) {
            saveToStorage(PROJECTS_KEY, state.projects);
        }
    }, [state.projects]);

    // Apply theme to document
    useEffect(() => {
        if (state.theme === 'dark') {
            document.documentElement.classList.add('dark');
        } else {
            document.documentElement.classList.remove('dark');
        }
        localStorage.setItem('theme', state.theme);
    }, [state.theme]);

    // Memoized actions
    const actions = useMemo(() => ({
        // Epic actions
        setEpics: (epics: Epic[]) => dispatch({ type: ActionTypes.SET_EPICS, payload: epics }),
        updateEpic: (id: string, updates: Partial<Epic>) => dispatch({ type: ActionTypes.UPDATE_EPIC, payload: { id, updates } }),
        addEpic: (epic: Epic) => dispatch({ type: ActionTypes.ADD_EPIC, payload: epic }),
        deleteEpic: (id: string) => dispatch({ type: ActionTypes.DELETE_EPIC, payload: id }),
        moveEpicToLane: (id: string, lane: EpicLane) => dispatch({ type: ActionTypes.MOVE_EPIC_TO_LANE, payload: { id, lane } }),

        // Project actions
        setProjects: (projects: Project[]) => dispatch({ type: ActionTypes.SET_PROJECTS, payload: projects }),
        addProject: (project: Project) => dispatch({ type: ActionTypes.ADD_PROJECT, payload: project }),
        updateProject: (id: string, updates: Partial<Project>) => dispatch({ type: ActionTypes.UPDATE_PROJECT, payload: { id, updates } }),
        deleteProject: (id: string) => dispatch({ type: ActionTypes.DELETE_PROJECT, payload: id }),
        setSelectedProjects: (ids: string[]) => dispatch({ type: ActionTypes.SET_SELECTED_PROJECT, payload: ids }),
        // Users
        setUsers: (users: User[]) => dispatch({ type: ActionTypes.SET_USERS, payload: users }),

        // Selection
        setSelectedId: (id: string | null) => dispatch({ type: ActionTypes.SET_SELECTED_ID, payload: id }),
        clearSelection: () => dispatch({ type: ActionTypes.CLEAR_SELECTION }),

        // View
        setActiveView: (view: string) => dispatch({ type: ActionTypes.SET_ACTIVE_VIEW, payload: view }),
        setSearch: (search: string) => dispatch({ type: ActionTypes.SET_SEARCH, payload: search }),

        // Theme
        setTheme: (theme: 'light' | 'dark') => dispatch({ type: ActionTypes.SET_THEME, payload: theme }),
        toggleTheme: () => dispatch({ type: ActionTypes.TOGGLE_THEME }),

        // Filters
        setFilters: (filters: Partial<AppFilters>) => dispatch({ type: ActionTypes.SET_FILTERS, payload: filters }),
        resetFilters: () => dispatch({ type: ActionTypes.RESET_FILTERS }),

        // Sessions
        setSessions: (sessions: Session[]) => dispatch({ type: ActionTypes.SET_SESSIONS, payload: sessions }),
        saveSession: (label: string) => dispatch({ type: ActionTypes.SAVE_SESSION, payload: label }),
        deleteSession: (id: string) => dispatch({ type: ActionTypes.DELETE_SESSION, payload: id }),

        // UI
        toggleSidebar: () => dispatch({ type: ActionTypes.TOGGLE_SIDEBAR }),
    }), []);

    // Computed values
    const computed = useMemo(() => {
        // Filter and sort epics - AND hydrate scores if missing
        let filteredEpics = state.epics.map(e => {
            if ((!e.wsjf || e.wsjf === 0) && (e.business || e.time)) {
                return { ...e, ...scoreEpic(e) };
            }
            return e;
        });

        // Calculate stats per project
        const projectsWithStats = state.projects.map(p => {
            const projectEpics = filteredEpics.filter(e => String(e.projectId) === String(p.id));
            const totalWsjf = projectEpics.reduce((sum, e) => sum + (e.wsjf || 0), 0);
            const avgWsjf = projectEpics.length > 0 ? totalWsjf / projectEpics.length : 0;

            // Calculate lane distribution
            const laneCounts = {
                now: projectEpics.filter(e => e.lane === 'Now').length,
                next: projectEpics.filter(e => e.lane === 'Next').length,
                later: projectEpics.filter(e => e.lane === 'Later').length,
                unassigned: projectEpics.filter(e => e.lane === 'Unassigned').length,
                done: projectEpics.filter(e => e.lane === 'Done').length,
            };
            const total = projectEpics.length;
            const laneDistribution = total > 0 ? {
                now: Math.round((laneCounts.now / total) * 100),
                next: Math.round((laneCounts.next / total) * 100),
                later: Math.round((laneCounts.later / total) * 100),
                unassigned: Math.round((laneCounts.unassigned / total) * 100),
                done: Math.round((laneCounts.done / total) * 100),
            } : { now: 0, next: 0, later: 0, unassigned: 0, done: 0 };

            return {
                ...p,
                epicCount: projectEpics.length,
                avgWsjf: avgWsjf,
                laneDistribution,
                doneCount: laneCounts.done,
            };
        });

        // Apply search filter
        if (state.search) {
            const searchLower = state.search.toLowerCase();
            filteredEpics = filteredEpics.filter(e => {
                if (e.title.toLowerCase().includes(searchLower)) return true;
                // Handle so as string or array
                const soText = Array.isArray(e.so) ? e.so.join(' ') : (e.so || '');
                if (soText.toLowerCase().includes(searchLower)) return true;
                // Handle arq as string or array
                const arqText = Array.isArray(e.arq) ? e.arq.join(' ') : (e.arq || '');
                if (arqText.toLowerCase().includes(searchLower)) return true;
                return false;
            });
        }

        // Apply lane filter (Multi-select OR logic - empty = all)
        if (state.filters.lane.length > 0) {
            filteredEpics = filteredEpics.filter(e => state.filters.lane.includes(e.lane));
        }

        // Apply owner filter (Multi-select OR logic)
        if (state.filters.owner.length > 0) {
            filteredEpics = filteredEpics.filter(e => {
                const epicOwners = Array.isArray(e.so) ? e.so : (e.so ? [e.so] : []);
                // Check for intersection
                return state.filters.owner.some(filterOwnerId => epicOwners.includes(filterOwnerId));
            });
        }

        // Apply Architect filter (Multi-select OR logic)
        if (state.filters.arq && state.filters.arq.length > 0) {
            filteredEpics = filteredEpics.filter(e => {
                const epicArqs = Array.isArray(e.arq) ? e.arq : (e.arq ? [e.arq] : []);
                // Check for intersection
                return state.filters.arq.some(filterArqId => epicArqs.includes(filterArqId));
            });
        }

        // Apply SGM filter
        if (state.filters.sgmOnly) {
            filteredEpics = filteredEpics.filter(e => e.sgm);
        }

        // Apply Priority filter (Multi-select OR logic - empty = all)
        if (state.filters.priority.length > 0) {
            filteredEpics = filteredEpics.filter(e => {
                const wsjf = e.wsjf || 0;
                return state.filters.priority.some(p => {
                    if (p === 'High') return wsjf > 15;
                    if (p === 'Medium') return wsjf >= 10 && wsjf <= 15;
                    if (p === 'Low') return wsjf < 10;
                    return false;
                });
            });
        }

        // Apply Project filter (Context Selector - Multi-select)
        if (state.selectedProjectIds.length > 0) {
            filteredEpics = filteredEpics.filter(e =>
                state.selectedProjectIds.includes(String(e.projectId)) ||
                (!e.projectId && state.selectedProjectIds.includes('project-legacy'))
            );
        }

        // Apply Project filter (Filter Panel - Multi-select OR logic)
        if (state.filters.projectId.length > 0) {
            filteredEpics = filteredEpics.filter(e => state.filters.projectId.includes(String(e.projectId)));
        }

        // Sort by WSJF
        filteredEpics.sort((a, b) => (b.wsjf || 0) - (a.wsjf || 0));

        // Get unique owners - handle both string and array formats
        const uniqueOwners = Array.from(new Set(state.epics.flatMap(e => {
            if (Array.isArray(e.so)) {
                return e.so;
            }
            return e.so?.split(',').map(s => s.trim()) || [];
        }))).filter(Boolean);

        // Get previous session for comparison
        const previousSession = state.sessions.length > 0 ? state.sessions[state.sessions.length - 1] : null;

        // Get previous score for an epic
        const getPreviousScore = (epicId: string) => {
            if (!previousSession) return undefined;
            const prevEpic = previousSession.epics.find(e => e.id === epicId);
            return prevEpic?.wsjf;
        };

        // Active filter count
        const activeFilterCount = [
            state.filters.lane.length > 0,
            state.filters.owner.length > 0,
            (state.filters.arq && state.filters.arq.length > 0),
            state.filters.sgmOnly,
            state.filters.priority.length > 0,
            state.filters.projectId.length > 0
        ].filter(Boolean).length;

        return {
            filteredEpics,
            uniqueOwners,
            previousSession,
            getPreviousScore,
            activeFilterCount,
            projectsWithStats,
        };
    }, [state.epics, state.search, state.filters, state.sessions, state.selectedProjectIds, state.projects]);

    const value = useMemo(() => ({
        state,
        actions,
        computed,
        dispatch,
    }), [state, actions, computed]);

    return (
        <AppContext.Provider value={value}>
            {children}
        </AppContext.Provider>
    );
}

// ============================================================================
// HOOKS
// ============================================================================

export function useApp() {
    const context = useContext(AppContext);
    if (!context) {
        throw new Error('useApp must be used within an AppProvider');
    }
    return context;
}

export function useEpics() {
    const { state, actions, computed } = useApp();
    return {
        epics: state.epics,
        filteredEpics: computed.filteredEpics,
        selectedId: state.selectedId,
        selectedEpic: state.epics.find(e => e.id === state.selectedId),
        updateEpic: actions.updateEpic,
        addEpic: actions.addEpic,
        deleteEpic: actions.deleteEpic,
        moveEpicToLane: actions.moveEpicToLane,
        setSelectedId: actions.setSelectedId,
        clearSelection: actions.clearSelection,
        getPreviousScore: computed.getPreviousScore,
    };
}

export function useTheme() {
    const { state, actions } = useApp();
    return {
        theme: state.theme,
        isDark: state.theme === 'dark',
        setTheme: actions.setTheme,
        toggleTheme: actions.toggleTheme,
    };
}

export function useFilters() {
    const { state, actions, computed } = useApp();
    return {
        filters: state.filters,
        setFilters: actions.setFilters,
        resetFilters: actions.resetFilters,
        activeFilterCount: computed.activeFilterCount,
        uniqueOwners: computed.uniqueOwners,
    };
}

export function useSessions() {
    const { state, actions, computed } = useApp();
    return {
        sessions: state.sessions,
        saveSession: actions.saveSession,
        deleteSession: actions.deleteSession,
        previousSession: computed.previousSession,
    };
}

export function useProjects() {
    const { state, actions, computed } = useApp();

    return {
        projects: state.projects,
        projectsWithStats: computed.projectsWithStats,
        selectedProjectIds: state.selectedProjectIds,
        selectedProjects: state.projects.filter(p => state.selectedProjectIds.includes(p.id)),
        selectedProjectEpics: computed.filteredEpics.filter(e => state.selectedProjectIds.includes(String(e.projectId))),
        addProject: actions.addProject,
        updateProject: actions.updateProject,
        deleteProject: actions.deleteProject,
        setSelectedProjects: actions.setSelectedProjects,
    };
}

export function useUsers() {
    const { state, actions } = useApp();
    return {
        users: state.users,
        setUsers: actions.setUsers,
    };
}

export default AppContext;
